


import { useState, useEffect } from 'react';
import { Layout, Spin, Row, Col, Card, message, Tabs, Input, Table } from 'antd';
import Sidebar from '../components/Sidebar';
import AppHeader from '../components/Header';
import { decryptData } from '../middleware/encryption';

const { Content } = Layout;
const { TabPane } = Tabs;
const { Search } = Input;

export default function Visualization() {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [countryData, setCountryData] = useState([]);
  const [industryData, setIndustryData] = useState([]);
  const [employeeSizeData, setEmployeeSizeData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [companyCountryData, setCompanyCountryData] = useState([]);
  const [companyIndustryData, setCompanyIndustryData] = useState([]);
  const [companyEmployeeSizeData, setCompanyEmployeeSizeData] = useState([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const myHeaders = new Headers();
        const token = sessionStorage.getItem('access_token');
        if (token) {
          myHeaders.append("Authorization", `Bearer ${token}`);
        }

        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow"
        };

        const [leadsResponse, companyResponse] = await Promise.all([
          fetch("https://api.app.exellius.com/api/leads/getPeopleLeadsDepartmentChartData", requestOptions),
          fetch("https://api.app.exellius.com/api/leads/getCompanyChartData", requestOptions)
        ]);

        const leadsResult = await leadsResponse.json();
        const companyResult = await companyResponse.json();

        if (leadsResult.data) {
          const decryptedLeadsResponse = decryptData(leadsResult.data).data;
          setDepartmentData(decryptedLeadsResponse.departments.map(item => ({ name: item[0], count: parseInt(item[1], 10) })));
          setCountryData(decryptedLeadsResponse.countries.map(item => ({ name: item[0], count: parseInt(item[1], 10) })));
          setIndustryData(decryptedLeadsResponse.industries.map(item => ({ name: item[0], count: parseInt(item[1], 10) })));
          setEmployeeSizeData(decryptedLeadsResponse.employee_sizes.map(item => ({ name: item[0], count: parseInt(item[1], 10) })));
        } else {
          message.error(leadsResult.message || "Failed to fetch leads data");
        }

        if (companyResult.data) {
          const decryptedCompanyResponse = decryptData(companyResult.data).data;
          setCompanyCountryData(decryptedCompanyResponse.location.map(item => ({ name: item[0], count: parseInt(item[1], 10) })));
          setCompanyIndustryData(decryptedCompanyResponse.industry.map(item => ({ name: item[0], count: parseInt(item[1], 10) })));
          setCompanyEmployeeSizeData(decryptedCompanyResponse.employee_size.map(item => ({ name: item[0], count: parseInt(item[1], 10) })));
        } else {
          message.error(companyResult.message || "Failed to fetch company data");
        }
      } catch (error) {
        console.error('Error fetching visualization data:', error);
        message.error("An error occurred while fetching visualization data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (e) => {
    setSearchText(e.target.value.toLowerCase());
  };

  const filterData = (data) => {
    return data.filter(item => item.name.toLowerCase().includes(searchText));
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
    },
  ];

  return (
    <Layout style={{ minHeight: '125vh' }}>
      <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />
      <Layout className="site-layout">
        <AppHeader />
        <Content style={{ margin: '0 16px', height: "calc(100vh - 64px)", overflowY: "scroll" }}>
          {loading ? (
            <Spin size="large" style={{ display: 'block', margin: 'auto', marginTop: '20%' }} />
          ) : (
            <Tabs defaultActiveKey="1">
              <TabPane tab="Leads" key="1">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Card title="Country">
                      <Search placeholder="Search Country" onChange={handleSearch} style={{ marginBottom: 16 }} />
                      <Table scroll={{ y: 500 }} columns={columns} dataSource={filterData(countryData)} rowKey="name" pagination={false} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Card title="Industry">
                      <Search placeholder="Search Industry" onChange={handleSearch} style={{ marginBottom: 16 }} />
                      <Table scroll={{ y: 500 }} columns={columns} dataSource={filterData(industryData)} rowKey="name" pagination={false} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Card title="Employee Size">
                      <Search placeholder="Search Employee Size" onChange={handleSearch} style={{ marginBottom: 16 }} />
                      <Table scroll={{ y: 500 }} columns={columns} dataSource={filterData(employeeSizeData)} rowKey="name" pagination={false} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Card title="Departments">
                      <Search placeholder="Search Department" onChange={handleSearch} style={{ marginBottom: 16 }} />
                      <Table scroll={{ y: 500 }} columns={columns} dataSource={filterData(departmentData)} rowKey="name" pagination={false} />
                    </Card>
                  </Col>
                </Row>
              </TabPane>
              <TabPane tab="Companies" key="2">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Card title="Country">
                      <Search placeholder="Search Country" onChange={handleSearch} style={{ marginBottom: 16 }} />
                      <Table scroll={{ y: 500 }} columns={columns} dataSource={filterData(companyCountryData)} rowKey="name" pagination={false} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Card title="Industry">
                      <Search placeholder="Search Industry" onChange={handleSearch} style={{ marginBottom: 16 }} />
                      <Table scroll={{ y: 500 }} columns={columns} dataSource={filterData(companyIndustryData)} rowKey="name" pagination={false} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Card title="Employee Size">
                      <Search placeholder="Search Employee Size" onChange={handleSearch} style={{ marginBottom: 16 }} />
                      <Table scroll={{ y: 500 }} columns={columns} dataSource={filterData(companyEmployeeSizeData)} rowKey="name" pagination={false} />
                    </Card>
                  </Col>
                </Row>
              </TabPane>
            </Tabs>
          )}
        </Content>
      </Layout>
    </Layout>
  );
}
